/*
XOL-467 — shared mechanics for the blocking date-chain validator.

Each of the four surfaces (ProjectTab, FinanceSharer, SharerAction,
LicenseeForm) owns its OWN `validateDates()` method — the copy differs per
surface (ProjectTab has no `expiry` input; Site -> Financials has no
`handover` input), so there is no single generic validator to share. What IS
shared is the mechanism this mixin provides:

  - `fieldErrors` — a component-local map of `{ [field]: message }`, read by
    each `<date-input :error="fieldErrors.started">` (or, for the two create
    forms' raw `<el-form-item :error="...">`, directly).
  - `notifyBlockedSave(count)` — the one toast: "Not saved — N dates need
    attention." Never repeats the per-field sentences.
  - `clearDateErrors()` — wipes the WHOLE map, not just one field. The rules
    are cross-field: fixing Handover can resolve a message shown on Agreement
    Start, so a field-scoped clear would leave a stale error on-screen.
  - `focusDateField(name)` — scrolls the first offending input into view.
    Works against BOTH `<date-input>` (el-form-item wraps an el-date-picker)
    and the create forms' raw `<el-date-picker>`, because it only needs the
    ref'd element itself. XOL-479: scroll-only, deliberately — it used to also
    `.focus()` the inner `<input>`, which opens an `el-date-picker`'s calendar
    panel and covers the error text directly beneath it (see the method's own
    comment).

Deliberately no `saving` flag: `PostMixins.send`'s view-only early return
fires neither callback, so a flag set here could stick `true` and lock the
edit toggle permanently. See docs/plans/2026-09-01-xol-467-date-chain-field-
validation.md.
------------------------------------------------------------------------ */

export default {
  data () {
    return {
      fieldErrors: {}
    }
  },

  methods: {
    // count is the number of DISTINCT fields carrying a message, not the
    // number of violated rules — two rules rerouted onto the same field
    // still read as "1 date needs attention".
    notifyBlockedSave (count) {
      this.$notify({
        title: 'Not saved',
        message: count + (count === 1 ? ' date needs' : ' dates need') +
          ' attention. The fields are marked below.',
        type: 'error',
        duration: 5500
      })
    },

    clearDateErrors () {
      this.fieldErrors = {}
    },

    // `name` matches the key used in `fieldErrors` / the `ref` attribute the
    // template put on that field (`ref="dateField_<name>"`). No-op if the
    // surface does not render that field — callers reroute to a field that
    // IS rendered before calling this.
    //
    // XOL-479: scroll only — do NOT `.focus()` the inner `<input>`. An
    // `el-date-picker` opens its calendar panel on focus, and that panel
    // renders directly over the error message this call exists to reveal
    // (verified live: docs/plans/2026-09-01-xol-467-date-chain-field-
    // validation.screenshots/live-03 vs live-04). Scrolling the field into
    // view is enough to orient the user; focusing would hide the very text
    // they need to read.
    focusDateField (name) {
      var self = this
      this.$nextTick(function () {
        var ref = self.$refs && self.$refs['dateField_' + name]
        if (!ref) return
        var el = ref.$el || ref
        if (!el) return
        if (el.scrollIntoView) {
          el.scrollIntoView({ block: 'center' })
        }
      })
    }
  }
}
